import { CircularProgress } from '@forgeglobal/design-framework';
import { Autocomplete, Box, TextField } from '@mui/material';
import {
  Provider,
  useProviderLookup,
} from '@tcs/client-portal-common/hooks/useProviderLookup';
import { FieldRenderProps } from '@tcs/common/components/atoms/Form';
import React, { useEffect, useState } from 'react';

interface ProviderAutocompleteProps extends FieldRenderProps<string> {
  label: string;
  fullWidth?: boolean;
  sx?: any;
  onProviderSelect?: (provider: Provider) => void;
  onProviderClear?: () => void;
  'data-test-id'?: string;
}

export const ProviderAutocomplete: React.FC<ProviderAutocompleteProps> = ({
  input,
  meta,
  label,
  fullWidth = false,
  sx = {},
  onProviderSelect,
  onProviderClear,
  'data-test-id': dataTestId,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState('');

  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null
  );

  const { data: providers = [], loading } = useProviderLookup(inputValue);

  useEffect(() => {
    setInputValue(input.value || '');
    if (!input.value) {
      setSelectedProvider(null);
    }
  }, [input.value]);

  useEffect(() => {
    if (input.value && providers.length) {
      const provider = providers.find((p) => p.name === input.value);
      setSelectedProvider(provider || null);
    }
  }, [input.value, providers]);

  const handleInputChange = (_, newValue: string) => {
    setInputValue(newValue);
    input.onChange(newValue);
    if (selectedProvider) {
      setSelectedProvider(null);
    }
  };

  const handleChange = (_, value: Provider | string | null) => {
    // Free text typed
    if (typeof value === 'string') {
      input.onChange(value);
      setInputValue(value);
      setSelectedProvider(null);
      return;
    }

    // Provider selected
    if (value) {
      input.onChange(value.name);
      setInputValue(value.name);
      setSelectedProvider(value);
      onProviderSelect?.(value);
      return;
    }

    input.onChange('');
    setInputValue('');
    setSelectedProvider(null);
    onProviderClear?.();
  };

  const isError = meta.touched && !!meta.error;

  return (
    <Box sx={{ mb: 1 }}>
      <Autocomplete
        {...rest}
        options={providers}
        getOptionLabel={(option: Provider | string) =>
          typeof option === 'string' ? option : option.name
        }
        isOptionEqualToValue={(option: Provider, value: Provider) =>
          option.id === value.id
        }
        freeSolo
        loading={loading}
        inputValue={inputValue}
        value={selectedProvider}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onBlur={input.onBlur}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="filled"
            fullWidth={fullWidth}
            error={isError}
            helperText={isError ? meta.error : undefined}
            sx={sx}
            data-test-id={dataTestId}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      sx={{ mt: -2 }}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            autoComplete="off"
          />
        )}
      />
    </Box>
  );
};

export default ProviderAutocomplete;
